import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, Center, Loader } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CustomerPortalApp } from './CustomerPortalApp';
import { CustomerLayout } from './CustomerLayout';
import { CustomerLoginPage } from './CustomerLoginPage';
import { CustomerVerifyPage } from './CustomerVerifyPage';
import { customerPortalTheme } from './customerPortalTheme';
import { TrackingProvider } from '../../lib/tracking';
import { useCustomerStore } from '../../stores/customerStore';
import { CustomerErrorBoundary } from './CustomerErrorBoundary';
import { ComingSoonPage } from './ComingSoonPage';
import { NoAccessPage } from './NoAccessPage';
import { usePortalAreaState } from './portalModules';
import type { PortalModuleId } from '../../lib/api.customer';

const CustomerDashboard = lazy(() =>
  import('./CustomerDashboard').then((m) => ({ default: m.CustomerDashboard }))
);
const CustomerTicketListPage = lazy(() =>
  import('./CustomerTicketListPage').then((m) => ({ default: m.CustomerTicketListPage }))
);
const CustomerTicketDetailPage = lazy(() =>
  import('./CustomerTicketDetailPage').then((m) => ({ default: m.CustomerTicketDetailPage }))
);
const CustomerSubmissionsPage = lazy(() =>
  import('./CustomerSubmissionsPage').then((m) => ({ default: m.CustomerSubmissionsPage }))
);
const CustomerAssetsPage = lazy(() =>
  import('./CustomerAssetsPage').then((m) => ({ default: m.CustomerAssetsPage }))
);
const CustomerAssetDetailPage = lazy(() =>
  import('./CustomerAssetDetailPage').then((m) => ({ default: m.CustomerAssetDetailPage }))
);
const CustomerDeadlinesPage = lazy(() =>
  import('./CustomerDeadlinesPage').then((m) => ({ default: m.CustomerDeadlinesPage }))
);
const CustomerAppointmentsPage = lazy(() =>
  import('./CustomerAppointmentsPage').then((m) => ({ default: m.CustomerAppointmentsPage }))
);
const CustomerProjectsPage = lazy(() =>
  import('./CustomerProjectsPage').then((m) => ({ default: m.CustomerProjectsPage }))
);
const CustomerInboxPage = lazy(() =>
  import('./CustomerInboxPage').then((m) => ({ default: m.CustomerInboxPage }))
);
const CustomerNewRequestPage = lazy(() =>
  import('./CustomerNewRequestPage').then((m) => ({ default: m.CustomerNewRequestPage }))
);
const CustomerEmergencyPage = lazy(() =>
  import('./CustomerEmergencyPage').then((m) => ({ default: m.CustomerEmergencyPage }))
);
const CustomerTrustPage = lazy(() =>
  import('./CustomerTrustPage').then((m) => ({ default: m.CustomerTrustPage }))
);
const CustomerDnsZonesPage = lazy(() =>
  import('./CustomerDnsZonesPage').then((m) => ({ default: m.CustomerDnsZonesPage }))
);
const CustomerAuditPage = lazy(() =>
  import('./CustomerAuditPage').then((m) => ({ default: m.CustomerAuditPage }))
);
const CustomerSettingsPage = lazy(() =>
  import('./CustomerSettingsPage').then((m) => ({ default: m.CustomerSettingsPage }))
);
const CustomerHelpPage = lazy(() =>
  import('./CustomerHelpPage').then((m) => ({ default: m.CustomerHelpPage }))
);
const CustomerReportsPage = lazy(() =>
  import('./CustomerReportsPage').then((m) => ({ default: m.CustomerReportsPage }))
);
const CustomerReportDetailPage = lazy(() =>
  import('./CustomerReportsPage').then((m) => ({ default: m.CustomerReportDetailPage }))
);
const CustomerQuotesPage = lazy(() =>
  import('./CustomerQuotesPage').then((m) => ({ default: m.CustomerQuotesPage }))
);
const CustomerTeamPage = lazy(() =>
  import('./CustomerTeamPage').then((m) => ({ default: m.CustomerTeamPage }))
);
const CustomerShopPage = lazy(() =>
  import('./CustomerShopPage').then((m) => ({ default: m.CustomerShopPage }))
);

// Eigener QueryClient für das Kunden-Portal (isoliert vom Techniker-Portal)
const customerQueryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

function CustomerSuspense({ children }: { children: React.ReactNode }) {
  return (
    <CustomerErrorBoundary>
      <Suspense fallback={<Center h={200}><Loader size="sm" color="orange" /></Center>}>
        {children}
      </Suspense>
    </CustomerErrorBoundary>
  );
}

/**
 * Bereich nur rendern, wenn diese Person hineinkommt. Für die Firma noch zu → „Demnächst";
 * für die Person abgeschaltet oder ohne Einlass-Recht → „Kein Zugriff". Der Server sperrt
 * ohnehin (403 MODULE_NOT_ENABLED); hier geht es darum, dass der Nutzer statt einer
 * Fehlermeldung eine Erklärung bekommt.
 */
function ModuleRoute({
  module,
  title,
  children,
}: {
  module: PortalModuleId;
  title: string;
  children: React.ReactNode;
}) {
  const state = usePortalAreaState(module);
  if (state === 'coming-soon') return <ComingSoonPage title={title} />;
  if (state === 'no-access') return <NoAccessPage title={title} module={module} />;
  return <CustomerSuspense>{children}</CustomerSuspense>;
}

/**
 * Telemetrie misst, wie Kunden das Portal nutzen — eine Mitarbeiter-Ansicht würde das
 * verfälschen. Der Server nähme die Events dort ohnehin nicht an (nur lesen).
 */
function TrackedCustomerLayout() {
  const isPreview = useCustomerStore((s) => !!s.contact?.preview);
  return (
    <TrackingProvider enabled={!isPreview}>
      <CustomerLayout />
    </TrackingProvider>
  );
}

/**
 * Kunden-Portal Routing-Baum.
 * Eigener MantineProvider + QueryClient – vollständig isoliert vom Techniker-Portal.
 */
export function CustomerPortalRoutes() {
  // Mantine schreibt `forceColorScheme` auf <html> — ein Dokument, zwei Portale.
  // Beim Verlassen des Kundenportals ohne Neuladen (Zurück-Taste, künftige
  // Links über die Grenze) blieb `light` stehen: das Technikerportal malt seine
  // Flächen hart dunkel (--fb-bg-elevated #242424), die Textfarbe kam aber aus
  // dem hellen Schema — dunkelgrau mit schwarzer Schrift, 1,35:1, auf jeder
  // Hover-Fläche. Deshalb hier zurückstellen, was hier gesetzt wurde.
  // Gleiches gilt für den Tab-Titel: index.html trägt den des Technikerportals,
  // Kunden sahen im Browser-Tab „Techniker-Portal".
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'FixByte IT - Kundenportal';
    return () => {
      document.title = previousTitle;
      document.documentElement.setAttribute('data-mantine-color-scheme', 'dark');
    };
  }, []);

  return (
    <QueryClientProvider client={customerQueryClient}>
      <MantineProvider theme={customerPortalTheme} forceColorScheme="light">
        {/* Task 1.3 — Notifications im isolierten Portal-Provider-Baum */}
        <Notifications position="top-right" zIndex={1400} />
        <Routes>
          <Route element={<CustomerPortalApp />}>
            {/* Öffentliche Routen */}
            <Route path="login" element={<CustomerLoginPage />} />
            <Route path="verify" element={<CustomerVerifyPage />} />

            {/* Task 1.2 — TrackingProvider wrapet alle geschützten Routen */}
            {/* Geschützte Routen unter CustomerLayout */}
            <Route element={<TrackedCustomerLayout />}>
              <Route
                path="dashboard"
                element={<CustomerSuspense><CustomerDashboard /></CustomerSuspense>}
              />
              <Route
                path="tickets"
                element={
                  <ModuleRoute module="tickets" title="Tickets">
                    <CustomerTicketListPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="tickets/:id"
                element={
                  <ModuleRoute module="tickets" title="Tickets">
                    <CustomerTicketDetailPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="submissions"
                element={
                  <ModuleRoute module="submissions" title="Serviceberichte">
                    <CustomerSubmissionsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="assets"
                element={
                  <ModuleRoute module="assets" title="Assets & Geräte">
                    <CustomerAssetsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="assets/:id"
                element={
                  <ModuleRoute module="assets" title="Assets & Geräte">
                    <CustomerAssetDetailPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="contracts"
                element={
                  <ModuleRoute module="contracts" title="Verträge & Lizenzen">
                    <CustomerDeadlinesPage />
                  </ModuleRoute>
                }
              />
              <Route path="deadlines" element={<Navigate to="/customer/contracts" replace />} />
              <Route
                path="appointments"
                element={
                  <ModuleRoute module="appointments" title="Termine">
                    <CustomerAppointmentsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="projects"
                element={
                  <ModuleRoute module="projects" title="Projekte">
                    <CustomerProjectsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="projects/:id"
                element={
                  <ModuleRoute module="projects" title="Projekte">
                    <CustomerProjectsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="inbox"
                element={
                  <ModuleRoute module="inbox" title="Postfach">
                    <CustomerInboxPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="requests"
                element={
                  <ModuleRoute module="requests" title="Anfragen">
                    <CustomerNewRequestPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="shop"
                element={
                  <ModuleRoute module="shop" title="Shop">
                    <CustomerShopPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="emergency"
                element={<CustomerSuspense><CustomerEmergencyPage /></CustomerSuspense>}
              />
              <Route
                path="quotes"
                element={
                  <ModuleRoute module="quotes" title="Angebote">
                    <CustomerQuotesPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ModuleRoute module="reports" title="Unsere IT">
                    <CustomerReportsPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="reports/:id"
                element={
                  <ModuleRoute module="reports" title="Unsere IT">
                    <CustomerReportDetailPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="help"
                element={<CustomerSuspense><CustomerHelpPage /></CustomerSuspense>}
              />
              <Route
                path="settings"
                element={<CustomerSuspense><CustomerSettingsPage /></CustomerSuspense>}
              />
              <Route
                path="settings/export"
                element={<CustomerSuspense><CustomerSettingsPage /></CustomerSuspense>}
              />
              <Route
                path="settings/team"
                element={<CustomerSuspense><CustomerTeamPage /></CustomerSuspense>}
              />
              <Route
                path="trust"
                element={
                  <ModuleRoute module="trust" title="Trust Center">
                    <CustomerTrustPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="audit"
                element={
                  <ModuleRoute module="audit" title="Protokoll">
                    <CustomerAuditPage />
                  </ModuleRoute>
                }
              />
              <Route
                path="dns"
                element={
                  <ModuleRoute module="dns" title="DNS-Zonen">
                    <CustomerDnsZonesPage />
                  </ModuleRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Routes>
      </MantineProvider>
    </QueryClientProvider>
  );
}

function PlaceholderPage({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 8px' }}>{title}</h2>
      <p style={{ color: '#888', margin: 0 }}>{note}</p>
    </div>
  );
}
